/**
 * lut_looks — authored LOOKS as 3D colour LUTs (2026-09-08, shader wave).
 *
 * The engine post pipeline has carried a `lut3D` pass (three's Lut3DNode) since
 * the gap-closure program, but Atlas never handed it a LUT. Instead of shipping
 * .cube files, each look is a pure colour function baked once into a 16³
 * `Data3DTexture` (RGBA8, linear filtering, no colour space) — the way the
 * classic film LUTs are authored, just procedurally: a lift/gamma/gain split by
 * luminance (teal shadows, orange highlights for 'cinematic'), a temperature
 * multiply ('warm' / 'cool'), a desaturated S-curve ('noir'), a violet lift
 * ('cosmos', the Commons at night). `lookColor` is the pure oracle the harness
 * checks; `bakeLook` caches one texture per look for the session.
 */
import * as THREE from 'three/webgpu';

export type LookName = 'none' | 'cinematic' | 'warm' | 'cool' | 'noir' | 'cosmos' | 'vivid';
export const LOOKS: LookName[] = ['none', 'cinematic', 'warm', 'cool', 'noir', 'cosmos', 'vivid'];
export const LOOK_LABEL: Record<LookName, string> = { none: 'Off', cinematic: 'Cinematic', warm: 'Warm', cool: 'Cool', noir: 'Noir', cosmos: 'Cosmos', vivid: 'Vivid' };
export const LUT_SIZE = 16;

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
const lum = (r: number, g: number, b: number): number => 0.2126 * r + 0.7152 * g + 0.0722 * b;
/** A gentle S-curve around mid grey; k = 1 is identity. */
const contrast = (v: number, k: number): number => clamp01(0.5 + (v - 0.5) * k);
const saturate = (r: number, g: number, b: number, s: number): [number, number, number] => {
  const l = lum(r, g, b);
  return [clamp01(l + (r - l) * s), clamp01(l + (g - l) * s), clamp01(l + (b - l) * s)];
};

/** The look as a pure function on linear-ish LDR RGB in [0,1]. */
export function lookColor(look: LookName, r: number, g: number, b: number): [number, number, number] {
  switch (look) {
    case 'none': return [r, g, b];
    case 'cinematic': {
      // Teal in the shadows, orange in the highlights, a touch more contrast.
      const l = lum(r, g, b);
      const sh = 1 - l, hi = l;
      let [x, y, z] = [r + (-0.02 * sh) + (0.035 * hi), g + (0.01 * sh) + (0.008 * hi), b + (0.045 * sh) + (-0.03 * hi)];
      [x, y, z] = [contrast(x, 1.08), contrast(y, 1.08), contrast(z, 1.08)];
      return saturate(x, y, z, 1.06);
    }
    case 'warm': return [clamp01(r * 1.05 + 0.006), clamp01(g * 1.0 + 0.003), clamp01(b * 0.93)];
    case 'cool': return [clamp01(r * 0.94), clamp01(g * 1.0 + 0.003), clamp01(b * 1.07 + 0.006)];
    case 'noir': {
      const [x, y, z] = saturate(r, g, b, 0.15);
      return [contrast(x, 1.25), contrast(y, 1.25), contrast(z, 1.25)];
    }
    case 'cosmos': {
      const l = lum(r, g, b);
      const sh = 1 - l;
      const [x, y, z] = saturate(clamp01(r + 0.03 * sh), clamp01(g + 0.0), clamp01(b + 0.05 * sh - 0.01 * l), 1.08);
      return [x, y, z];
    }
    case 'vivid': {
      const [x, y, z] = saturate(r, g, b, 1.2);
      return [contrast(x, 1.05), contrast(y, 1.05), contrast(z, 1.05)];
    }
  }
}

const cache = new Map<LookName, THREE.Data3DTexture>();

/** Bake (once per look) the look into a 16³ RGBA8 3D texture for the engine's `lut3D` pass. */
export function bakeLook(look: LookName, size = LUT_SIZE): THREE.Data3DTexture {
  const hit = cache.get(look);
  if (hit) return hit;
  const data = new Uint8Array(size * size * size * 4);
  let i = 0;
  for (let bi = 0; bi < size; bi++) for (let gi = 0; gi < size; gi++) for (let ri = 0; ri < size; ri++) {
    const [r, g, b] = lookColor(look, ri / (size - 1), gi / (size - 1), bi / (size - 1));
    data[i++] = Math.round(r * 255); data[i++] = Math.round(g * 255); data[i++] = Math.round(b * 255); data[i++] = 255;
  }
  const tex = new THREE.Data3DTexture(data, size, size, size);
  tex.format = THREE.RGBAFormat;
  tex.type = THREE.UnsignedByteType;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.wrapS = tex.wrapT = tex.wrapR = THREE.ClampToEdgeWrapping;
  tex.colorSpace = THREE.NoColorSpace;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  cache.set(look, tex);
  return tex;
}
